import {
   AfterViewInit,
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   HostListener,
   Input,
   NgZone,
   OnChanges,
   OnDestroy,
   OnInit,
   Output,
   SimpleChanges,
   ViewChild
} from '@angular/core';
import { Chart, dispose, init, KLineData, LoadDataParams, LoadDataType } from 'klinecharts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Logger } from '../logging/logger.interface';
import { LoggerService } from '../logging/logger-factory.service';
import { PriceChartDataFeed } from './price-chart-data-feed';
import { PriceChartDataFeedFactoryService } from './price-chart-data-feed-factory.service';
import { PIVOT_INDICATOR_NAME, registerPivotIndicator } from './price-chart-pivots-indicator';

export interface OverlaySnapshot {
   name: string;
   points: Array<Partial<{ timestamp: number; dataIndex: number; value: number }>>;
   styles?: any;
   lock?: boolean;
   visible?: boolean;
   zLevel?: number;
}

const RESOLUTION_DURATION_MS: { [k: string]: number } = {
   '1':   60_000,
   '5':   300_000,
   '15':  900_000,
   '30':  1_800_000,
   '60':  3_600_000,
   '240': 14_400_000,
   'D':   86_400_000,
   'W':   604_800_000
};

const RESOLUTION_DENSITY_FACTOR: { [k: string]: number } = {
   '1':   3.5,
   '5':   3.5,
   '15':  3.5,
   '30':  3.5,
   '60':  3.5,
   '240': 3.5,
   'D':   1.5,
   'W':   1.0
};

const RESOLUTION_SEED_CAPS_MS: { [k: string]: { min: number; max: number } } = {
   '1':   { min: 5 * 86_400_000,       max: 30  * 86_400_000 },
   '5':   { min: 86_400_000,           max: 30  * 86_400_000 },
   '15':  { min: 3   * 86_400_000,     max: 90  * 86_400_000 },
   '30':  { min: 3   * 86_400_000,     max: 90  * 86_400_000 },
   '60':  { min: 7   * 86_400_000,     max: 365 * 86_400_000 },
   '240': { min: 30  * 86_400_000,     max: 5   * 365 * 86_400_000 },
   'D':   { min: 365 * 86_400_000,     max: 50  * 365 * 86_400_000 },
   'W':   { min: 5   * 365 * 86_400_000, max: 100 * 365 * 86_400_000 }
};

const RESOLUTION_PROBE_LOOKBACK_MS: { [k: string]: number } = {
   '1':   30  * 86_400_000,
   '5':   30  * 86_400_000,
   '15':  30  * 86_400_000,
   '30':  30  * 86_400_000,
   '60':  180 * 86_400_000,
   '240': 180 * 86_400_000,
   'D':   5   * 365 * 86_400_000,
   'W':   20  * 365 * 86_400_000
};

const DEFAULT_BAR_SPACE_PX = 6;

@Component({
   selector: 'ets-price-chart-canvas',
   template: `
      <div class="chart-host" #host></div>
      <dx-load-panel
            [visible]="isLoading"
            [position]="{ my: 'center', at: 'center', of: host }"
            [shading]="false"
            [showIndicator]="true"
            [showPane]="true">
      </dx-load-panel>
   `,
   styles: [
      `:host { display: block; width: 100%; height: 100%; position: relative; }`,
      `.chart-host { width: 100%; height: 100%; background-color: #1e1e1e; }`
   ],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

   constructor(
      private readonly _ngZone: NgZone,
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _feedFactory: PriceChartDataFeedFactoryService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger('PriceChartComponent');
   }

   @ViewChild('host', { static: true }) hostRef: ElementRef<HTMLDivElement>;

   @Input() componentId: string;
   @Input() ticker: string;
   @Input() resolution: string;
   @Input() chartType: string = 'candle_solid';
   @Input() maEnabled = false;
   @Input() maPeriod = 20;
   @Input() maColor = '#26a69a';
   @Input() pivotsEnabled = false;
   @Input() pivotUpColor = '#26a69a';
   @Input() pivotDownColor = '#ef5350';

   @Output() overlaysChanged = new EventEmitter<OverlaySnapshot[]>();
   @Output() barsLoaded = new EventEmitter<void>();

   isLoading = false;

   private readonly _logger: Logger;
   private _chart: Chart | null = null;
   private _feed: PriceChartDataFeed | null = null;
   private _subscriptionKey: string | null = null;
   private _maPaneId: string | null = null;
   private _pivotsPaneId: string | null = null;
   private _selectedOverlayId: string | null = null;
   private readonly _overlays = new Map<string, OverlaySnapshot>();
   private _suppressOverlayEmits = false;
   private readonly _unsubscriber = new Subject<void>();
   private _initialized = false;
   private _loadingPromise: Promise<void> | null = null;
   private _loadCounter = 0;
   private _hostResizeObserver: ResizeObserver | null = null;
   private _hostResizeRafId: number | null = null;
   private _lastObservedHostW = 0;
   private _lastObservedHostH = 0;

   ngOnInit(): void {
      this.isLoading = true;
   }

   ngAfterViewInit(): void {
      setTimeout(() => this._bootstrap(), 0);
   }

   private _bootstrap(): void {
      registerPivotIndicator();

      const fontFamily = this._resolveHostFontFamily();
      this._ngZone.runOutsideAngular(() => {
         this._chart = init(this.hostRef.nativeElement, {
            styles: this._darkStyles(fontFamily),
            timezone: 'America/New_York'
         } as any);
         this._chart!.setLoadDataCallback((params: LoadDataParams) => {
            this._onLoadData(params);
         });
      });

      this._feed = this._feedFactory.getDataFeed(this.componentId);

      this._feed.bars$
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(live => {
            if (live.subscriptionKey !== this._subscriptionKey || !this._chart) {
               return;
            }
            this._ngZone.runOutsideAngular(() => this._chart!.updateData(live.bar));
         });

      this._feed.reload$
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(() => this._reload());

      this._initialized = true;
      this._applyChartType();
      this._reload();
      this._applyMA();
      this._applyPivots();

      this._installHostResizeObserver();
   }

   // klinecharts only observes its own canvas, not the outer container; window/tab
   // size changes leave the canvas stale until next chart.resize() externally.
   private _installHostResizeObserver(): void {
      if (typeof ResizeObserver === 'undefined') {
         return;
      }
      const host = this.hostRef.nativeElement;
      this._lastObservedHostW = host.clientWidth;
      this._lastObservedHostH = host.clientHeight;
      this._hostResizeObserver = new ResizeObserver(() => {
         const w = host.clientWidth;
         const h = host.clientHeight;
         if (w === 0 || h === 0) {
            this._lastObservedHostW = w;
            this._lastObservedHostH = h;
            return;
         }
         if (w === this._lastObservedHostW && h === this._lastObservedHostH) {
            return;
         }
         this._lastObservedHostW = w;
         this._lastObservedHostH = h;
         if (this._hostResizeRafId !== null) {
            cancelAnimationFrame(this._hostResizeRafId);
         }
         this._hostResizeRafId = requestAnimationFrame(() => {
            this._hostResizeRafId = null;
            this.resize();
         });
      });
      this._hostResizeObserver.observe(host);
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (!this._initialized) {
         return;
      }
      if (changes.ticker || changes.resolution) {
         this._reload();
      }
      if (changes.chartType) {
         this._applyChartType();
         if (this.pivotsEnabled) {
            this._applyPivots();
         }
      }
      if (changes.maEnabled || changes.maPeriod || changes.maColor) {
         this._applyMA();
      }
      if (changes.pivotsEnabled || changes.pivotUpColor || changes.pivotDownColor) {
         this._applyPivots();
      }
   }

   ngOnDestroy(): void {
      this._unsubscriber.next();
      this._unsubscriber.complete();

      if (this._hostResizeRafId !== null) {
         cancelAnimationFrame(this._hostResizeRafId);
         this._hostResizeRafId = null;
      }
      if (this._hostResizeObserver) {
         this._hostResizeObserver.disconnect();
         this._hostResizeObserver = null;
      }

      if (this._subscriptionKey && this._feed) {
         this._feed.unsubscribeBars(this._subscriptionKey).catch(() => {});
         this._subscriptionKey = null;
      }

      if (this._feed) {
         this._feed.dispose();
         this._feed = null;
      }

      if (this._chart) {
         dispose(this.hostRef.nativeElement);
         this._chart = null;
      }
   }

   resize(): void {
      if (!this._chart) {
         return;
      }
      const chart = this._chart;
      this._ngZone.runOutsideAngular(() => {
         chart.resize();
         // klinecharts resize() updates canvas pixel dims but not the data-viewport
         // state derived from prior container width. Replay the last bar to force
         // the full pipeline (same path a live tick takes).
         requestAnimationFrame(() => {
            const list = chart.getDataList();
            if (list.length > 0) {
               chart.updateData(list[list.length - 1]);
            }
         });
      });
   }

   createOverlay(name: string): void {
      if (!this._chart) {
         return;
      }
      const overlay: any = this._buildOverlayCreate({ name });
      this._ngZone.runOutsideAngular(() => this._chart!.createOverlay(overlay));
   }

   removeAllOverlays(): void {
      if (!this._chart) {
         return;
      }
      this._selectedOverlayId = null;
      const had = this._overlays.size > 0;
      this._suppressOverlayEmits = true;
      try {
         this._ngZone.runOutsideAngular(() => this._chart!.removeOverlay());
      } finally {
         this._suppressOverlayEmits = false;
      }
      this._overlays.clear();
      if (had) {
         this._emitOverlays();
      }
   }

   applyOverlays(snaps: OverlaySnapshot[]): void {
      if (!this._chart) {
         return;
      }
      this._suppressOverlayEmits = true;
      try {
         this._ngZone.runOutsideAngular(() => this._chart!.removeOverlay());
         this._overlays.clear();
         this._selectedOverlayId = null;
         for (const snap of snaps) {
            const create = this._buildOverlayCreate(snap);
            let id: string | null = null;
            this._ngZone.runOutsideAngular(() => {
               const result = this._chart!.createOverlay(create);
               id = typeof result === 'string' ? result : null;
            });
            if (id) {
               this._overlays.set(id, snap);
            }
         }
      } finally {
         this._suppressOverlayEmits = false;
      }
   }

   private _buildOverlayCreate(snap: { name: string;
                                       points?: Array<Partial<{ timestamp: number; dataIndex: number; value: number }>>;
                                       styles?: any;
                                       lock?: boolean;
                                       visible?: boolean;
                                       zLevel?: number }): any {
      return {
         name: snap.name,
         points: snap.points,
         styles: snap.styles,
         lock: snap.lock,
         visible: snap.visible,
         zLevel: snap.zLevel,
         onDrawEnd: (event: any) => {
            const id = event && event.overlay ? event.overlay.id : null;
            if (id) {
               this._overlays.set(id, this._extractSnapshot(event.overlay));
               this._emitOverlays();
            }
            return false;
         },
         onPressedMoveEnd: (event: any) => {
            const id = event && event.overlay ? event.overlay.id : null;
            if (id && this._overlays.has(id)) {
               this._overlays.set(id, this._extractSnapshot(event.overlay));
               this._emitOverlays();
            }
            return false;
         },
         onSelected: (event: any) => {
            this._selectedOverlayId = event && event.overlay ? event.overlay.id : null;
            return false;
         },
         onDeselected: (event: any) => {
            const id = event && event.overlay ? event.overlay.id : null;
            if (id && id === this._selectedOverlayId) {
               this._selectedOverlayId = null;
            }
            return false;
         },
         onRemoved: (event: any) => {
            const id = event && event.overlay ? event.overlay.id : null;
            if (id) {
               if (id === this._selectedOverlayId) {
                  this._selectedOverlayId = null;
               }
               if (this._overlays.delete(id)) {
                  this._emitOverlays();
               }
            }
            return false;
         }
      };
   }

   private _extractSnapshot(overlay: any): OverlaySnapshot {
      const rawPoints = Array.isArray(overlay.points) ? overlay.points : [];
      return {
         name: overlay.name,
         points: rawPoints.map((p: any) => ({ ...p })),
         styles: overlay.styles,
         lock: overlay.lock,
         visible: overlay.visible,
         zLevel: overlay.zLevel
      };
   }

   private _emitOverlays(): void {
      if (this._suppressOverlayEmits) {
         return;
      }
      const snaps = Array.from(this._overlays.values());
      this._ngZone.run(() => this.overlaysChanged.emit(snaps));
   }

   scrollToRealTime(): void {
      if (!this._chart) {
         return;
      }
      this._ngZone.runOutsideAngular(() => this._chart!.scrollToRealTime(250));
   }

   @HostListener('window:keydown', ['$event'])
   onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Delete' && event.key !== 'Del') {
         return;
      }
      const tgt = event.target as HTMLElement | null;
      if (tgt) {
         const tag = tgt.tagName;
         if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tgt.isContentEditable) {
            return;
         }
      }
      if (!this._chart || !this._selectedOverlayId) {
         return;
      }
      const id = this._selectedOverlayId;
      this._selectedOverlayId = null;
      this._ngZone.runOutsideAngular(() => {
         try { this._chart!.removeOverlay(id); } catch { /* ignore */ }
      });
   }

   private _applyChartType(): void {
      if (!this._chart) {
         return;
      }
      const type = this.chartType || 'candle_solid';
      this._ngZone.runOutsideAngular(() => {
         try {
            this._chart!.setStyles({ candle: { type } } as any);
         } catch { /* ignore */ }
      });
   }

   private _applyMA(): void {
      if (!this._chart) {
         return;
      }
      const period = Number.isFinite(this.maPeriod) && this.maPeriod > 0 ? Math.floor(this.maPeriod) : 0;
      const color = this.maColor || '#26a69a';

      if (this.maEnabled && period > 0) {
         const create: any = {
            name: 'MA',
            calcParams: [period],
            styles: {
               lines: [{
                  style: 'solid',
                  smooth: false,
                  size: 1,
                  dashedValue: [2, 2],
                  color
               }]
            }
         };
         this._ngZone.runOutsideAngular(() => {
            try {
               if (this._maPaneId) {
                  this._chart!.overrideIndicator(create, this._maPaneId);
               } else {
                  this._maPaneId = this._chart!.createIndicator(create, true, { id: 'candle_pane' });
               }
            } catch {
               this._maPaneId = null;
            }
         });
      } else {
         if (this._maPaneId) {
            const paneId = this._maPaneId;
            this._maPaneId = null;
            this._ngZone.runOutsideAngular(() => {
               try { this._chart!.removeIndicator(paneId, 'MA'); } catch { /* ignore */ }
            });
         }
      }
   }

   private _applyPivots(): void {
      if (!this._chart) {
         return;
      }

      if (this.pivotsEnabled) {
         const polygonBase = {
            style: 'fill',
            borderStyle: 'solid',
            borderSize: 1,
            borderDashedValue: [2, 2]
         };
         const upColor = this.pivotUpColor || '#26a69a';
         const downColor = this.pivotDownColor || '#ef5350';
         const create: any = {
            name: PIVOT_INDICATOR_NAME,
            calcParams: [this.chartType || 'candle_solid'],
            styles: {
               circles: [
                  { ...polygonBase, upColor, downColor: upColor, noChangeColor: upColor },
                  { ...polygonBase, upColor: downColor, downColor, noChangeColor: downColor }
               ]
            }
         };
         this._ngZone.runOutsideAngular(() => {
            try {
               if (this._pivotsPaneId) {
                  this._chart!.overrideIndicator(create, this._pivotsPaneId);
               } else {
                  this._pivotsPaneId = this._chart!.createIndicator(create, true, { id: 'candle_pane' });
               }
            } catch {
               this._pivotsPaneId = null;
            }
         });
      } else {
         if (this._pivotsPaneId) {
            const paneId = this._pivotsPaneId;
            this._pivotsPaneId = null;
            this._ngZone.runOutsideAngular(() => {
               try { this._chart!.removeIndicator(paneId, PIVOT_INDICATOR_NAME); } catch { /* ignore */ }
            });
         }
      }
   }

   private _reload(): void {
      if (!this._feed || !this._chart || !this.ticker || !this.resolution) {
         return;
      }
      const ticker = this.ticker;
      const resolution = this.resolution;

      const previousKey = this._subscriptionKey;
      this._subscriptionKey = null;

      this._loadCounter++;
      const myLoad = this._loadCounter;
      this._setLoading(true);

      const swap = (async () => {
         if (previousKey && this._feed) {
            await this._feed.unsubscribeBars(previousKey).catch(() => {});
         }

         const { from, to } = this._computeSeedWindow(new Date(), resolution);
         this._logRequest('seed', ticker, resolution, from, to);

         let bars: KLineData[] = [];
         let lastDto = null;
         let stats = null;
         try {
            const result = await this._feed!.loadHistory(ticker, resolution, from, to);
            bars = result.bars;
            lastDto = result.lastDto;
            stats = result.stats;
         } catch {
            if (myLoad === this._loadCounter) {
               this._setLoading(false);
            }
            return;
         }

         if (!this._chart || myLoad !== this._loadCounter) {
            if (myLoad === this._loadCounter) {
               this._setLoading(false);
            }
            return;
         }

         this._logReceived('seed', ticker, resolution, bars, stats);
         this._logPlotted('seed', ticker, resolution, bars, true);
         this._ngZone.runOutsideAngular(() => this._chart!.applyNewData(bars, true));

         if (myLoad === this._loadCounter) {
            this._ngZone.run(() => this.barsLoaded.emit());
         }

         if (lastDto) {
            const key = await this._feed!.subscribeBars(ticker, resolution, lastDto);
            if (key && myLoad === this._loadCounter) {
               this._subscriptionKey = key;
            }
         }

         if (myLoad === this._loadCounter) {
            this._setLoading(false);
         }
      })();

      this._loadingPromise = swap;
   }

   private _onLoadData(params: LoadDataParams): void {
      // klinecharts naming is inverted vs the typical chart-library convention:
      //   LoadDataType.Forward  => older direction (prepend, leftward scroll)
      //   LoadDataType.Backward => newer direction (append, rightward scroll)
      // We only fetch in the older direction; live updates own the newer side.

      if (params.type === LoadDataType.Backward) {
         // Newer direction — disable forever; live stream owns this side.
         params.callback([], false);
         return;
      }

      if (params.type !== LoadDataType.Forward) {
         params.callback([], false);
         return;
      }

      if (!this._feed || !this.ticker || !this.resolution || !params.data) {
         params.callback([], false);
         return;
      }

      const ticker = this.ticker;
      const resolution = this.resolution;
      const anchorTime = params.data.timestamp;
      const myLoad = this._loadCounter;

      this._setLoading(true);

      void (async () => {
         try {
            const primary = this._computeBackwardWindow(anchorTime, resolution);
            this._logRequest('forward.primary', ticker, resolution, primary.from, primary.to);

            const result = await this._feed!.loadHistory(ticker, resolution, primary.from, primary.to);

            if (!this._chart || myLoad !== this._loadCounter || ticker !== this.ticker || resolution !== this.resolution) {
               params.callback([], true);
               return;
            }

            this._logReceived('forward.primary', ticker, resolution, result.bars, result.stats);

            // Backend returns bars AT and sometimes PAST the requested `to` boundary
            // (IQFeed end-of-window semantics + slot-end timestamping). For the Forward
            // (older) direction the anchor IS the chart's current oldest bar, so any
            // bar with ts >= anchor is already plotted — re-feeding them creates
            // duplicate-prepend loops and ghost-renders. Filter strictly < anchor.
            const primaryOlder = result.bars.filter(b => b.timestamp < anchorTime);
            this._logger.info(`chart.forward.primary.filter ticker=${ticker} res=${resolution} raw=${result.bars.length} olderThanAnchor=${primaryOlder.length} anchorTs=${new Date(anchorTime).toISOString()}`);

            if (primaryOlder.length > 0) {
               this._logPlotted('forward.primary', ticker, resolution, primaryOlder, true);
               params.callback(primaryOlder, true);
               return;
            }

            // Two-shot exhaustion probe per ADR-012: empty primary result is ambiguous
            // (could be weekend/holiday/after-hours gap rather than genuine end-of-history).
            // Issue a wide-window probe before declaring exhausted.
            const probeLookback = RESOLUTION_PROBE_LOOKBACK_MS[resolution];
            if (!probeLookback) {
               this._logger.warn(`chart.no-probe-config ticker=${ticker} res=${resolution}`);
               params.callback([], false);
               return;
            }

            const probeFrom = new Date(anchorTime - probeLookback);
            const probeTo = new Date(anchorTime);
            this._logRequest('forward.probe', ticker, resolution, probeFrom, probeTo);

            const probe = await this._feed!.loadHistory(ticker, resolution, probeFrom, probeTo);

            if (!this._chart || myLoad !== this._loadCounter || ticker !== this.ticker || resolution !== this.resolution) {
               params.callback([], true);
               return;
            }

            this._logReceived('forward.probe', ticker, resolution, probe.bars, probe.stats);

            const probeOlder = probe.bars.filter(b => b.timestamp < anchorTime);
            this._logger.info(`chart.forward.probe.filter ticker=${ticker} res=${resolution} raw=${probe.bars.length} olderThanAnchor=${probeOlder.length} anchorTs=${new Date(anchorTime).toISOString()}`);

            if (probeOlder.length > 0) {
               this._logPlotted('forward.probe', ticker, resolution, probeOlder, true);
               params.callback(probeOlder, true);
               return;
            }

            this._logger.info(`chart.exhausted ticker=${ticker} res=${resolution} oldestLoadedBarTs=${new Date(anchorTime).toISOString()}`);
            params.callback([], false);
         } catch (e) {
            this._logger.error(`chart.error ticker=${ticker} res=${resolution} err=${e}`);
            params.callback([], true);
         } finally {
            if (myLoad === this._loadCounter) {
               this._setLoading(false);
            }
         }
      })();
   }

   private _computeSeedWindow(now: Date, resolution: string): { from: Date; to: Date } {
      const duration = this._computeWindowDuration(resolution);
      const to = now;
      const from = new Date(to.getTime() - duration);
      return { from, to };
   }

   private _computeBackwardWindow(anchorTime: number, resolution: string): { from: Date; to: Date } {
      const duration = this._computeWindowDuration(resolution);
      const to = new Date(anchorTime);
      const from = new Date(anchorTime - duration);
      return { from, to };
   }

   private _computeWindowDuration(resolution: string): number {
      const resMs = RESOLUTION_DURATION_MS[resolution];
      const density = RESOLUTION_DENSITY_FACTOR[resolution];
      const caps = RESOLUTION_SEED_CAPS_MS[resolution];
      if (!resMs || !density || !caps) {
         return 30 * 86_400_000;
      }

      let barSpace = DEFAULT_BAR_SPACE_PX;
      try {
         const live = this._chart ? this._chart.getBarSpace() : 0;
         if (Number.isFinite(live) && live > 0) {
            barSpace = live;
         }
      } catch {
         // fallback to default
      }

      const containerWidth = this.hostRef && this.hostRef.nativeElement
         ? this.hostRef.nativeElement.clientWidth
         : 0;
      const visibleBarCount = containerWidth > 0 ? Math.floor(containerWidth / barSpace) : 0;

      let durationMs: number;
      if (visibleBarCount <= 0) {
         durationMs = caps.min;
      } else {
         const seedBars = Math.ceil(visibleBarCount * 1.30);
         durationMs = Math.ceil(seedBars * resMs * density);
      }

      if (durationMs < caps.min) durationMs = caps.min;
      if (durationMs > caps.max) durationMs = caps.max;
      return durationMs;
   }

   private _logRequest(
      source: 'seed' | 'forward.primary' | 'forward.probe',
      ticker: string,
      resolution: string,
      from: Date,
      to: Date
   ): void {
      this._logger.info(`chart.request source=${source} ticker=${ticker} res=${resolution} from=${from.toISOString()} to=${to.toISOString()} durationMs=${to.getTime() - from.getTime()}`);
   }

   private _logReceived(
      source: 'seed' | 'forward.primary' | 'forward.probe',
      ticker: string,
      resolution: string,
      bars: KLineData[],
      stats: { rawCount: number; duplicateTsCount: number; zeroRangeBarCount: number; zeroRangeBarDropped: number; monotonicAscending: boolean } | null
   ): void {
      const rawCount = stats ? stats.rawCount : bars.length;
      const dupTs = stats ? stats.duplicateTsCount : 0;
      const zeroRange = stats ? stats.zeroRangeBarCount : 0;
      const zeroRangeDropped = stats ? stats.zeroRangeBarDropped : 0;
      const mono = stats ? stats.monotonicAscending : true;
      const firstTs = bars.length ? new Date(bars[0].timestamp).toISOString() : 'none';
      const lastTs = bars.length ? new Date(bars[bars.length - 1].timestamp).toISOString() : 'none';
      this._logger.info(`chart.received source=${source} ticker=${ticker} res=${resolution} raw=${rawCount} kept=${bars.length} dupTs=${dupTs} zeroRange=${zeroRange} zeroRangeDropped=${zeroRangeDropped} monoAsc=${mono} firstTs=${firstTs} lastTs=${lastTs}`);
   }

   private _logPlotted(
      source: 'seed' | 'forward.primary' | 'forward.probe',
      ticker: string,
      resolution: string,
      bars: KLineData[],
      more: boolean
   ): void {
      const firstTs = bars.length ? new Date(bars[0].timestamp).toISOString() : 'none';
      const lastTs = bars.length ? new Date(bars[bars.length - 1].timestamp).toISOString() : 'none';
      this._logger.info(`chart.plotted source=${source} ticker=${ticker} res=${resolution} count=${bars.length} more=${more} firstTs=${firstTs} lastTs=${lastTs}`);
   }

   private _setLoading(value: boolean): void {
      if (this.isLoading === value) {
         return;
      }
      this.isLoading = value;
      this._changeDetector.detectChanges();
   }

   private _resolveHostFontFamily(): string {
      try {
         const cs = window.getComputedStyle(this.hostRef.nativeElement);
         const family = (cs && cs.fontFamily) ? cs.fontFamily.trim() : '';
         return family || 'Helvetica Neue, Helvetica, Arial, sans-serif';
      } catch {
         return 'Helvetica Neue, Helvetica, Arial, sans-serif';
      }
   }

   private _darkStyles(fontFamily: string) {
      return {
         grid: {
            horizontal: { color: '#333333' },
            vertical: { color: '#333333' }
         },
         candle: {
            bar: {
               upColor: '#26a69a',
               downColor: '#ef5350',
               upBorderColor: '#26a69a',
               downBorderColor: '#ef5350',
               upWickColor: '#26a69a',
               downWickColor: '#ef5350'
            },
            tooltip: {
               text:  { color: '#cccccc', family: fontFamily },
               title: { color: '#cccccc', family: fontFamily },
               rect:  { },
               legend: { text: { color: '#cccccc', family: fontFamily } }
            },
            priceMark: {
               high: { color: '#cccccc', family: fontFamily },
               low:  { color: '#cccccc', family: fontFamily },
               last: { text: { family: fontFamily } }
            }
         },
         indicator: {
            tooltip: {
               text:  { color: '#cccccc', family: fontFamily },
               title: { color: '#cccccc', family: fontFamily }
            }
         },
         xAxis: {
            axisLine: { color: '#666666' },
            tickText: { color: '#cccccc', family: fontFamily }
         },
         yAxis: {
            axisLine: { color: '#666666' },
            tickText: { color: '#cccccc', family: fontFamily }
         },
         crosshair: {
            horizontal: { line: { color: '#666666' }, text: { backgroundColor: '#444444', family: fontFamily } },
            vertical:   { line: { color: '#666666' }, text: { backgroundColor: '#444444', family: fontFamily } }
         }
      };
   }
}
